import React, { useEffect, useReducer, useState, useRef } from 'react'
import '../styles/navbar.css'
import { useSpring, useTransition, animated } from 'react-spring'
import { Link } from "react-router-dom";
import logo from '../static/pictures/logo.png';
import question from '../static/pictures/question.png';
import temperatureIcon from '../static/pictures/temperature.png';
import cloudyIcon from '../static/pictures/cloudy.png';
import humidityIcon from '../static/pictures/humidity.png';
import { getCameraPics } from '../functions/getCameraPics';
import { getCameraVisibility } from '../functions/getCameraVisibility';
import { seasonChecking } from '../functions/seasonChecking';
import { getOpeningHours } from '../functions/getOpeningHours';
import { getWeatherData } from '../functions/getWeatherData';
import Otvorenie from '../components/Otvorenie.js';
import { HashLink } from 'react-router-hash-link';
import { trackEvent } from '../functions/analytics';

const Sidebar = ({ isMobile, setOpenSideBar, openSideBar }) => {

    const [cameraPics, setCameraPics] = useState("");
    const [imgCamera, setImgCamera] = useState();
    const [imgCameraTwo, setImgCameraTwo] = useState();
    const [zoom, setZoom] = useState(0);
    const [cameraHidden, setCameraHidden] = useState(false);
    const [cameraHiddenMessage, setCameraHiddenMessage] = useState('');
    const currentImageFilenameRef = useRef(null);

    const isSummer = seasonChecking();

    const [openingLoading, setOpeningLoading] = useState(true);
    const [weather, setWeather] = useState({});
    const [currentStatus, setCurrentStatus] = useState({
        openSlopes: '',
        openLifts: '',
        snowDepth: ''
    });

    const fetchCameraData = async () => {
        try {
            // Admin can hide the camera from the dashboard - check that before loading a frame.
            const visibility = await getCameraVisibility();
            if (visibility && visibility.hidden) {
                setCameraHidden(true);
                setCameraHiddenMessage(visibility.message || '');
                setZoom(0);
                setImgCamera((prev) => {
                    if (prev) URL.revokeObjectURL(prev);
                    return undefined;
                });
                currentImageFilenameRef.current = null;
                return;
            }
            setCameraHidden(false);

            const cameraData = await getCameraPics();
            setCameraPics(cameraData);

            if (!cameraData || cameraData.length === 0) {
                console.warn('No camera data available.');
                return;
            }

            const newImageFilename = cameraData[0];
            
            // Only update if the image filename has changed
            if (currentImageFilenameRef.current === newImageFilename) {
                console.log('Image unchanged, skipping update:', newImageFilename);
                return;
            }

            console.log('New image detected, updating:', newImageFilename);

            const url = process.env.REACT_APP_API_HOST + '/static/webp/' + newImageFilename;
            console.log('Fetching image from:', url);

            const res = await fetch(url);
            if (!res.ok) {
                console.error(`Failed to fetch image: ${res.status} ${res.statusText}`, url);
                console.error('Response headers:', Object.fromEntries(res.headers.entries()));
                throw new Error(`Failed to fetch: ${res.status} ${res.statusText}`);
            }

            // Check content length from headers
            const contentLength = res.headers.get('content-length');
            console.log('Content-Length header:', contentLength);
            
            if (contentLength === '0' || (contentLength && parseInt(contentLength) === 0)) {
                console.warn('Server returned empty file (Content-Length: 0) for:', newImageFilename);
                return;
            }

            const imageBlob = await res.blob();
            console.log('Received blob, size:', imageBlob.size, 'type:', imageBlob.type);
            
            // Check if blob is empty or invalid
            if (!imageBlob || imageBlob.size === 0) {
                console.warn('Received empty blob for image:', newImageFilename, 'URL:', url);
                console.warn('This might mean the webp file does not exist or conversion failed');
                return;
            }
            
            // Check if blob type is correct
            if (!imageBlob.type.startsWith('image/')) {
                console.warn('Received non-image blob:', imageBlob.type, 'for file:', newImageFilename);
                return;
            }

            // Clean up previous object URL to prevent memory leaks
            if (imgCamera) {
                URL.revokeObjectURL(imgCamera);
            }

            const imageObjectURL = URL.createObjectURL(imageBlob);
            setImgCamera(imageObjectURL);
            currentImageFilenameRef.current = newImageFilename;

            console.log('Image loaded successfully, size:', imageBlob.size, 'bytes');
        } catch (error) {
            console.error('Error fetching image:', error);
            // Handle errors, e.g., retry or display a placeholder image
        }
    };

    useEffect(() => {
        const loadData = async () => {
            await fetchCameraData();
        };

        // Initial load
        loadData();

        // Cameras refresh every 10 seconds
        const intervalId = setInterval(loadData, 10000);

        // Cleanup the interval and object URL when the component is unmounted
        return () => {
            clearInterval(intervalId);
            if (imgCamera) {
                URL.revokeObjectURL(imgCamera);
            }
        };
    }, []);

    useEffect(() => {
        const loadOpening = async () => {
            try {
                const data = await getOpeningHours();
                if (data.ok && data.data && data.data.currentStatus) {
                    setCurrentStatus(data.data.currentStatus);
                }
            } catch (e) {
                console.error('Error loading opening hours for sidebar:', e);
            } finally {
                setOpeningLoading(false);
            }
        };
        loadOpening();
    }, [isSummer]);

    useEffect(() => {
        const loadWeather = async () => {
            try {
                const weatherData = await getWeatherData();
                if (weatherData && weatherData.data) {
                    setWeather(weatherData.data);
                }
            } catch (e) {
                console.error('Error loading weather data for sidebar:', e);
            }
        };
        loadWeather();
    }, []);

    const transition = useTransition(openSideBar, {
        from: { right: '-800px' },
        enter: { right: '0px' },
        leave: { right: '-800px' }
    });

    return transition((style, item) => {
        return !isMobile ? <>

            {zoom === 1 && !cameraHidden ? <div className="zoom">
                <img style={{ cursor: 'pointer' }} onClick={() => setZoom(0)} src={imgCamera} />
                <p></p>
            </div> : ""}

            <div className="sidenav">
                <div style={isSummer ? { paddingBottom: '30px', color: '#84d9ad' } : { paddingBottom: '30px', color: 'aqua' }} className="daily-header">Záznam z kamery</div>

                {cameraHidden ? (
                    <p className="camera-hidden-note" style={{ marginLeft: '30px', marginRight: '30px' }}>
                        {cameraHiddenMessage || 'Záznam z kamery je momentálne nedostupný.'}
                    </p>
                ) : (
                    <div className="zaznamy-kamier" style={{ justifyContent: 'left', marginLeft: '30px' }}>
                        <img style={{ cursor: 'pointer' }} onClick={() => setZoom(1)} src={imgCamera} />
                    </div>
                )}

                <div style={{ marginTop: '10px', marginBottom: '20px', marginLeft: '30px' }}>
                    {openingLoading ? (
                        <p style={{ color: 'white' }}>Načítavanie informácií o stave strediska...</p>
                    ) : (
                        <table className="otvorenie styled-table" style={{ width: 'auto' }}>
                            <tbody>
                                <tr>
                                    <td>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                                            <path d="M4 4L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 8L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 12L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Svahy v prevádzke
                                    </td>
                                    <td>{currentStatus.openSlopes || 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                                            <path d="M4 4L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 8L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 12L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Vleky v prevádzke
                                    </td>
                                    <td>{currentStatus.openLifts || 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <img src={humidityIcon} alt="Sneh" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                                        Počet cm snehu
                                    </td>
                                    <td>{currentStatus.snowDepth ? `${currentStatus.snowDepth} cm` : 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <img src={temperatureIcon} alt="Teplota" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                                        Teplota
                                    </td>
                                    <td>{weather.main?.temp ? `${weather.main.temp} °C` : 'X'}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={isSummer ? { paddingBottom: '30px', color: '#84d9ad' } : { paddingBottom: '30px', color: 'aqua' }} className="daily-header">Otváracie hodiny</div>
                <Otvorenie></Otvorenie>
                <div className="bot">
                    <img src={logo} alt="logo" />
                </div>
            </div></> : item && isMobile ? <animated.div style={{...style, overflowY: 'auto', paddingBottom: '120px'}} className="sidenav">
                <button onClick={() => setOpenSideBar(false)} id="hide">X</button>
                <a onClick={() => setOpenSideBar(false)} href="https://skiland.sk/">Úvod</a>
                {/*  <Link onClick={() => setOpenSideBar(false)} to='/archive'>Archív</Link> */}
                <Link onClick={() => setOpenSideBar(false)} to='/events'>Podujatia</Link>
                <Link onClick={() => { setOpenSideBar(false); trackEvent('camera_nav_click', { event_category: 'camera', event_label: 'sidebar_link' }); }} to='/cameras'>Záznamy kamier</Link>
                <HashLink onClick={() => setOpenSideBar(false)} to='/#cennik-id'>Cenník</HashLink>
                
                <div style={isSummer ? { paddingBottom: '30px', color: '#84d9ad' } : { paddingBottom: '30px', color: 'aqua' }} className="daily-header">Záznam z kamery</div>
                {cameraHidden ? (
                    <p className="camera-hidden-note" style={{ width: 'calc(100% - 30px)', margin: '0 auto 20px auto' }}>
                        {cameraHiddenMessage || 'Záznam z kamery je momentálne nedostupný.'}
                    </p>
                ) : (
                    <div style={{ width: 'calc(100% - 30px)', margin: '0 auto 20px auto', boxSizing: 'border-box' }}>
                        <img src={imgCamera} alt="Camera" style={{ width: '100%', borderRadius: '20px', display: 'block' }} />
                    </div>
                )}

                <div style={{ marginTop: '10px', marginBottom: '20px', marginLeft: '30px' }}>
                    {openingLoading ? (
                        <p style={{ color: 'white' }}>Načítavanie informácií o stave strediska...</p>
                    ) : (
                        <table className="otvorenie styled-table" style={{ width: 'auto' }}>
                            <tbody>
                                <tr>
                                    <td>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                                            <path d="M4 4L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 8L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 12L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Svahy v prevádzke
                                    </td>
                                    <td>{currentStatus.openSlopes || 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px', verticalAlign: 'middle', display: 'inline-block' }}>
                                            <path d="M4 4L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 8L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                            <path d="M4 12L20 20" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                        Vleky v prevádzke
                                    </td>
                                    <td>{currentStatus.openLifts || 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <img src={humidityIcon} alt="Sneh" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                                        Počet cm snehu
                                    </td>
                                    <td>{currentStatus.snowDepth ? `${currentStatus.snowDepth} cm` : 'X'}</td>
                                </tr>
                                <tr>
                                    <td>
                                        <img src={temperatureIcon} alt="Teplota" style={{ width: '18px', marginRight: '8px', verticalAlign: 'middle' }} />
                                        Teplota
                                    </td>
                                    <td>{weather.main?.temp ? `${weather.main.temp} °C` : 'X'}</td>
                                </tr>
                            </tbody>
                        </table>
                    )}
                </div>

                <div style={{ paddingBottom: '30px' }} className="daily-header">Otváracie hodiny</div>
                <Otvorenie></Otvorenie>
                <div style={{ position: 'relative', width: '100%', textAlign: 'center', padding: '20px 0', marginTop: '20px' }}>
                    <img src={logo} alt="logo" style={{ height: '60px' }} />
                </div>
            </animated.div> : ""
    });
}

export default Sidebar